/**
 * Poker Multiplayer Game - Real-time poker with WebSocket
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Wifi, WifiOff, Send, Users, Crown, 
  MessageCircle, X, Copy, Check, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { SUITS, BOOSTY_LOGO_URL } from './pokerConstants.js';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

// Poker Card Component
const PokerCard = ({ card, faceDown, small }) => {
  if (!card) return null;
  
  if (faceDown) {
    return (
      <motion.div
        initial={{ opacity: 0, rotateY: 180 }}
        animate={{ opacity: 1, rotateY: 0 }}
        className={`${small ? 'w-10 h-14' : 'w-14 h-20'} rounded-lg relative overflow-hidden`}
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 50%, #1e1b4b 100%)',
          border: '2px solid #a855f7',
          boxShadow: '0 2px 8px rgba(139,92,246,0.3)'
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={BOOSTY_LOGO_URL} alt="" className={`${small ? 'h-6' : 'h-10'} w-auto object-contain opacity-90`} />
        </div>
      </motion.div>
    );
  }
  
  const suitInfo = SUITS[card.suit];
  
  return (
    <motion.div
      initial={{ opacity: 0, rotateY: 180 }}
      animate={{ opacity: 1, rotateY: 0 }}
      className={`${small ? 'w-10 h-14' : 'w-14 h-20'} rounded-lg relative bg-white`}
      style={{ border: '1px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
    >
      <div className="absolute top-0.5 left-1 flex flex-col items-center leading-none">
        <span className={`${small ? 'text-xs' : 'text-sm'} font-black`} style={{ color: suitInfo?.color || '#000' }}>{card.display}</span>
        <span className={`${small ? 'text-[8px]' : 'text-xs'}`} style={{ color: suitInfo?.color || '#000' }}>{suitInfo?.symbol || '?'}</span>
      </div>
      <div className="absolute bottom-0.5 right-1 flex flex-col items-center leading-none rotate-180">
        <span className={`${small ? 'text-xs' : 'text-sm'} font-black`} style={{ color: suitInfo?.color || '#000' }}>{card.display}</span>
        <span className={`${small ? 'text-[8px]' : 'text-xs'}`} style={{ color: suitInfo?.color || '#000' }}>{suitInfo?.symbol || '?'}</span>
      </div>
    </motion.div>
  );
};

// Player Seat Component
const PlayerSeat = ({ player, isCurrentTurn, position, totalPlayers, myId }) => {
  const getPositionStyle = () => {
    if (totalPlayers === 2) {
      const positions = {
        0: { bottom: '60px', left: '50%', transform: 'translateX(-50%)' },
        1: { top: '50px', left: '50%', transform: 'translateX(-50%)' }
      };
      return positions[position] || positions[0];
    }
    
    const positions = {
      0: { bottom: '60px', left: '50%', transform: 'translateX(-50%)' },
      1: { top: '50%', left: '20px', transform: 'translateY(-50%)' },
      2: { top: '50px', left: '12%' },
      3: { top: '50px', left: '50%', transform: 'translateX(-50%)' },
      4: { top: '50px', right: '12%' },
      5: { top: '50%', right: '20px', transform: 'translateY(-50%)' }
    };
    return positions[position] || positions[0];
  };
  
  const isMe = player.id === myId;
  
  return (
    <div className="absolute flex flex-col items-center gap-1 z-10" style={getPositionStyle()}>
      <div className={`px-3 py-1.5 rounded-lg text-xs ${
        isCurrentTurn ? 'bg-amber-500/40 border-2 border-amber-400' : 
        player.folded ? 'bg-red-900/40 border border-red-800/50' : 
        isMe ? 'bg-emerald-500/30 border border-emerald-400/50' :
        'bg-slate-800/90 border border-slate-600'
      }`}>
        <div className="flex items-center gap-1.5">
          {player.is_dealer && <span className="w-4 h-4 rounded-full bg-amber-500 text-[8px] font-bold flex items-center justify-center text-black">D</span>}
          <span className={`font-bold ${player.folded ? 'text-red-400 line-through' : isMe ? 'text-emerald-400' : 'text-white'}`}>
            {player.name} {isMe && '(You)'}
          </span>
        </div>
        <div className="flex items-center justify-center gap-1 text-[10px]">
          <span className="text-amber-400">{player.chips}</span>
          {player.current_bet > 0 && <span className="text-cyan-400">({player.current_bet})</span>}
          {player.all_in && <span className="text-red-400 font-bold">ALL-IN</span>}
        </div>
      </div>
      
      {/* Hole cards */}
      {player.hole_cards && player.hole_cards.length > 0 && !player.folded && (
        <div className="flex -space-x-1">
          {player.hole_cards.map((card, idx) => (
            <PokerCard key={idx} card={card} faceDown={!card.suit} small />
          ))}
        </div>
      )}
    </div>
  );
};

// Chat Component
const ChatPanel = ({ messages, onSend, isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (message.trim()) {
      onSend(message.trim());
      setMessage('');
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      className="absolute right-0 top-0 bottom-0 w-72 bg-slate-900/98 border-l border-slate-700 flex flex-col z-30"
    >
      <div className="p-3 border-b border-slate-700 flex items-center justify-between">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <MessageCircle className="w-4 h-4" /> Chat
        </h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.map((msg, idx) => (
          <div key={idx} className="text-sm">
            <span className="font-semibold text-emerald-400">{msg.player}: </span>
            <span className="text-slate-300">{msg.message}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="p-3 border-t border-slate-700">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type a message..."
            className="flex-1 bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm text-white placeholder-slate-500"
          />
          <Button size="sm" onClick={handleSend} className="bg-emerald-600 hover:bg-emerald-500">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const PokerMultiplayer = () => {
  const { tableId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const playerName = location.state?.playerName || 'Player_' + Math.random().toString(36).substring(2, 6);
  
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [showChat, setShowChat] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    setConnecting(true);
    setError(null);
    
    const ws = new WebSocket(`${WS_URL}/api/ws/poker/${tableId}`);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      ws.send(JSON.stringify({
        type: 'join',
        player_id: `player_${Date.now()}`,
        name: playerName
      }));
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WS message:', data);
      
      switch (data.type) {
        case 'joined':
          setPlayerId(data.player_id);
          setConnected(true);
          setConnecting(false);
          break;
        case 'game_state':
          setGameState(data.data);
          break;
        case 'chat':
          setChatMessages(prev => [...prev, { player: data.player, message: data.message }]);
          break;
        case 'player_joined':
          setChatMessages(prev => [...prev, { player: 'System', message: `${data.player.name} joined the table` }]);
          break;
        case 'player_left':
          setChatMessages(prev => [...prev, { player: 'System', message: `A player left the table` }]);
          break;
        case 'error':
          setError(data.message);
          setConnecting(false);
          break;
        default:
          break;
      }
    };
    
    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setError('Connection error');
      setConnecting(false);
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed');
      setConnected(false);
      // Try to reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          connect();
        }
      }, 3000);
    };
  }, [tableId, playerName]);

  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const sendAction = (action, amount = 0) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'action', action, amount }));
    }
  };

  const sendChat = (message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chat', message }));
    }
  };

  const startHand = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'start_hand' }));
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/games/poker/lobby?join=${tableId}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const myPlayer = gameState?.players?.find(p => p.id === playerId);
  const isMyTurn = myPlayer && gameState?.players[gameState?.current_player_index]?.id === playerId;
  const canStart = gameState?.phase === 'waiting' && gameState?.players?.length >= 2;

  // Calculate valid actions
  const getValidActions = () => {
    if (!isMyTurn || !myPlayer) return [];
    const toCall = (gameState?.current_bet || 0) - (myPlayer.current_bet || 0);
    const actions = ['fold'];
    if (toCall === 0) actions.push('check');
    else if (myPlayer.chips >= toCall) actions.push('call');
    if (myPlayer.chips > toCall) actions.push('raise');
    if (myPlayer.chips > 0) actions.push('all_in');
    return actions;
  };

  const validActions = getValidActions();
  const toCall = (gameState?.current_bet || 0) - (myPlayer?.current_bet || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/20 to-slate-950 relative overflow-hidden">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/games/poker/lobby')} className="text-slate-400 hover:text-white h-8">
              <ArrowLeft className="w-4 h-4 mr-1" /> Lobby
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white">Multiplayer Poker</h1>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <code className="bg-slate-800 px-1.5 py-0.5 rounded">{tableId}</code>
                <span className={`flex items-center gap-1 ${connected ? 'text-green-400' : 'text-red-400'}`}>
                  {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                  {connected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyInviteLink}
              className="border-slate-600 text-slate-300 h-8"
            >
              {copiedLink ? <Check className="w-4 h-4 mr-1 text-green-400" /> : <Copy className="w-4 h-4 mr-1" />}
              {copiedLink ? 'Copied!' : 'Invite'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowChat(!showChat)}
              className="text-slate-400 hover:text-white h-8"
            >
              <MessageCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto p-4 relative">
        {connecting ? (
          <div className="flex flex-col items-center justify-center h-96">
            <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin mb-4" />
            <p className="text-white">Connecting to table...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-96">
            <WifiOff className="w-12 h-12 text-red-400 mb-4" />
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={connect} className="bg-emerald-600 hover:bg-emerald-500">
              Reconnect
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-3">
            {/* Action Log */}
            <div className="col-span-3">
              <div className="bg-slate-900/80 rounded-xl p-3 border border-slate-700 max-h-64 overflow-y-auto">
                <h4 className="text-xs text-slate-500 font-semibold mb-2">Action Log</h4>
                <div className="space-y-0.5">
                  {(gameState?.action_log || []).map((entry, idx) => (
                    <div key={idx} className={`text-xs ${entry.startsWith('---') ? 'text-amber-400 font-bold' : 'text-slate-400'}`}>
                      {entry}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Betting Controls */}
              {isMyTurn && validActions.length > 0 && (
                <div className="mt-3 bg-slate-900/95 rounded-xl p-3 border-2 border-amber-500/50">
                  <div className="text-amber-400 text-xs font-bold mb-2 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    Your Turn {toCall > 0 && <span className="text-slate-400">({toCall} to call)</span>}
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5">
                    {validActions.includes('fold') && (
                      <Button size="sm" onClick={() => sendAction('fold')} variant="outline" className="border-red-600 text-red-400 h-8 px-3 text-xs">
                        Fold
                      </Button>
                    )}
                    {validActions.includes('check') && (
                      <Button size="sm" onClick={() => sendAction('check')} variant="outline" className="border-slate-600 text-slate-300 h-8 px-3 text-xs">
                        Check
                      </Button>
                    )}
                    {validActions.includes('call') && (
                      <Button size="sm" onClick={() => sendAction('call')} className="bg-blue-600 hover:bg-blue-500 h-8 px-3 text-xs">
                        Call {toCall}
                      </Button>
                    )}
                    {validActions.includes('all_in') && (
                      <Button size="sm" onClick={() => sendAction('all_in')} className="bg-red-600 hover:bg-red-500 h-8 px-3 text-xs">
                        All-In
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Start Game Button */}
              {canStart && (
                <div className="mt-3">
                  <Button onClick={startHand} className="w-full bg-emerald-600 hover:bg-emerald-500">
                    Start Hand
                  </Button>
                </div>
              )}
            </div>
            
            {/* Table */}
            <div className="col-span-9">
              <div 
                className="relative rounded-[40px] border-4 border-emerald-700/60 p-4 h-[520px]"
                style={{
                  background: 'radial-gradient(ellipse at center, #166534 0%, #14532d 40%, #052e16 100%)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                {/* Phase Message */}
                <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20">
                  <div className="bg-slate-900/90 px-4 py-1.5 rounded-lg border border-slate-600 text-xs">
                    <span className="text-white font-semibold">
                      {gameState?.phase === 'waiting' 
                        ? `Waiting for players (${gameState?.players?.length || 0}/6)`
                        : gameState?.phase?.toUpperCase()}
                    </span>
                  </div>
                </div>
                
                {/* Community Cards + Pot */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3 z-20">
                  {/* Community Cards */}
                  <div className="flex gap-2">
                    {(gameState?.community_cards || []).map((card, idx) => (
                      <PokerCard key={idx} card={card} />
                    ))}
                    {/* Placeholders */}
                    {Array.from({ length: Math.max(0, 5 - (gameState?.community_cards?.length || 0)) }).map((_, idx) => (
                      <div key={`placeholder-${idx}`} className="w-14 h-20 rounded-lg border-2 border-dashed border-slate-600/50" />
                    ))}
                  </div>
                  
                  {/* Pot */}
                  {(gameState?.pot || 0) > 0 && (
                    <div className="bg-amber-900/70 px-4 py-1.5 rounded-lg border border-amber-500/50">
                      <span className="text-amber-300 font-bold">Pot: {gameState.pot}</span>
                    </div>
                  )}
                </div>
                
                {/* Player Seats */}
                {(gameState?.players || []).map((player, idx) => (
                  <PlayerSeat
                    key={player.id}
                    player={player}
                    position={idx}
                    totalPlayers={gameState.players.length}
                    isCurrentTurn={idx === gameState.current_player_index}
                    myId={playerId}
                  />
                ))}
                
                {/* No players message */}
                {(!gameState?.players || gameState.players.length === 0) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">Waiting for players to join...</p>
                      <Button onClick={copyInviteLink} variant="outline" className="mt-3 border-emerald-500/50 text-emerald-400">
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Invite Link
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Chat Panel */}
      <AnimatePresence>
        {showChat && (
          <ChatPanel
            messages={chatMessages}
            onSend={sendChat}
            isOpen={showChat}
            onClose={() => setShowChat(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PokerMultiplayer;
